import { useState, useRef, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isToday,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, LayoutGrid, Columns } from 'lucide-react';
import { Button } from '@werkstatt/ui';
import { Badge } from '@werkstatt/ui';
import { Card, CardContent } from '@werkstatt/ui';
import api from '../../lib/api';
import type { CalendarEntry } from '../../types';
import { CalendarEntryDialog } from './CalendarEntryDialog';
import { MainLayout } from '../../components/MainLayout';
import { cn } from '../../lib/utils';

interface ContextMenu {
  x: number;
  y: number;
  date: string;
}

type ViewMode = 'month' | 'week';

const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function isoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function isoWeekday(d: Date): number {
  return (getDay(d) + 6) % 7;
}

function weekStart(d: Date): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

function weekEnd(d: Date): Date {
  return endOfWeek(d, { weekStartsOn: 1 });
}

function useCalendarEntries(dates: Date[]) {
  const months = [...new Set(dates.map((d) => `${d.getFullYear()}-${d.getMonth() + 1}`))];

  const results = useQueries({
    queries: months.map((key) => {
      const [year, month] = key.split('-').map(Number);
      return {
        queryKey: ['calendar', year, month],
        queryFn: async (): Promise<CalendarEntry[]> => {
          const response = await api.get('/calendar', { params: { year, month } });
          return response.data;
        },
      };
    }),
  });

  const allEntries = results.flatMap((r) => r.data ?? []);
  const seen = new Set<number>();
  return allEntries.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
}

export default function CalendarPage() {
  const [view, setView] = useState<ViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<CalendarEntry | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // Derive view-specific date ranges
  const monthDays = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });
  const weekDays = eachDayOfInterval({ start: weekStart(currentDate), end: weekEnd(currentDate) });
  const visibleDays = view === 'month' ? monthDays : weekDays;

  const entries = useCalendarEntries(visibleDays);

  const entriesForDay = useCallback(
    (day: Date) => {
      const iso = isoDate(day);
      return entries.filter((e) => e.date_start <= iso && e.date_end >= iso);
    },
    [entries],
  );

  const handleDayRightClick = (e: React.MouseEvent, day: Date) => {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    setContextMenu({
      x: e.clientX - (rect?.left ?? 0),
      y: e.clientY - (rect?.top ?? 0),
      date: isoDate(day),
    });
  };

  const closeContextMenu = () => setContextMenu(null);

  const openNewEntry = (date: string) => {
    setSelectedEntry(null);
    setDefaultDate(date);
    setDialogOpen(true);
    closeContextMenu();
  };

  const openEditEntry = (entry: CalendarEntry) => {
    setSelectedEntry(entry);
    setDefaultDate(undefined);
    setDialogOpen(true);
  };

  const handleDialogSuccess = (y: number, m: number) => {
    setCurrentDate(new Date(y, m - 1, 1));
  };

  const goToday = () => setCurrentDate(new Date());

  const goPrev = () =>
    setCurrentDate((d) => (view === 'month' ? subMonths(d, 1) : subWeeks(d, 1)));

  const goNext = () =>
    setCurrentDate((d) => (view === 'month' ? addMonths(d, 1) : addWeeks(d, 1)));

  const navLabel =
    view === 'month'
      ? format(currentDate, 'MMMM yyyy', { locale: de })
      : `KW ${getISOWeek(weekStart(currentDate))} · ${format(weekStart(currentDate), 'dd.', { locale: de })} – ${format(weekEnd(currentDate), 'dd. MMMM yyyy', { locale: de })}`;

  return (
    <MainLayout>
      <div className="flex flex-col gap-4" onClick={closeContextMenu}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <CalendarDays className="h-7 w-7" />
              Kalender
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex rounded-md border overflow-hidden">
              <Button
                variant={view === 'month' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-0 gap-1.5"
                onClick={() => setView('month')}
              >
                <LayoutGrid className="h-4 w-4" />
                Monat
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-0 border-l gap-1.5"
                onClick={() => setView('week')}
              >
                <Columns className="h-4 w-4" />
                Woche
              </Button>
            </div>

            {/* Navigation */}
            <Button variant="outline" size="sm" onClick={goPrev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold min-w-[220px] text-center">{navLabel}</span>
            <Button variant="outline" size="sm" onClick={goNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday}>
              Heute
            </Button>

            <Button size="sm" onClick={() => openNewEntry(isoDate(new Date()))}>
              <Plus className="h-4 w-4 mr-1" />
              Neuer Eintrag
            </Button>
          </div>
        </div>

        {/* ── Calendar area ── */}
        <Card variant="glass">
          <CardContent className="p-4">
        <div ref={containerRef} className="relative select-none">

          {/* ════ MONTH VIEW ════ */}
          {view === 'month' && (
            <>
              {/* Weekday headers */}
              <div className="grid grid-cols-7 mb-1">
                {WEEKDAYS_SHORT.map((d) => (
                  <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: isoWeekday(monthDays[0]) }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}

                {monthDays.map((day) => {
                  const dayEntries = entriesForDay(day);
                  const today = isToday(day);
                  return (
                    <Card
                      key={isoDate(day)}
                      onContextMenu={(e) => handleDayRightClick(e, day)}
                      className={cn(
                        'min-h-[100px] cursor-default transition-colors hover:bg-accent/50',
                        today && 'ring-2 ring-primary ring-inset',
                      )}
                    >
                      <CardContent className="p-1.5">
                        <div
                          className={cn(
                            'text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full',
                            today ? 'bg-primary text-primary-foreground' : 'text-foreground',
                          )}
                        >
                          {format(day, 'd')}
                        </div>
                        <div className="space-y-0.5">
                          {dayEntries.slice(0, 3).map((entry) => (
                            <button
                              key={entry.id}
                              className="w-full text-left"
                              onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }}
                            >
                              <Badge
                                className="w-full truncate text-xs justify-start font-normal bg-primary/15 text-primary hover:bg-primary/25 border-0 cursor-pointer"
                                title={`${entry.license_plate} · ${entry.renter_name}`}
                              >
                                {entry.license_plate} · {entry.renter_name}
                              </Badge>
                            </button>
                          ))}
                          {dayEntries.length > 3 && (
                            <span className="text-xs text-muted-foreground pl-1">
                              +{dayEntries.length - 3} weitere
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {/* ════ WEEK VIEW ════ */}
          {view === 'week' && (
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day, idx) => {
                const dayEntries = entriesForDay(day);
                const today = isToday(day);
                return (
                  <div key={isoDate(day)} className="flex flex-col gap-1">
                    {/* Column header */}
                    <div
                      className={cn(
                        'text-center py-2 rounded-md text-sm font-semibold',
                        today ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                      )}
                    >
                      <div>{WEEKDAYS_LONG[idx]}</div>
                      <div className={cn('text-lg font-bold', today ? 'text-primary-foreground' : 'text-foreground')}>
                        {format(day, 'd')}
                      </div>
                      <div className="text-xs font-normal opacity-75">
                        {format(day, 'MMM', { locale: de })}
                      </div>
                    </div>

                    {/* Drop zone / entries */}
                    <div
                      className="flex-1 min-h-[400px] rounded-md border bg-card p-1.5 space-y-1.5 cursor-default hover:bg-accent/30 transition-colors"
                      onContextMenu={(e) => handleDayRightClick(e, day)}
                    >
                      {dayEntries.length === 0 && (
                        <div className="h-full flex items-start justify-center pt-6 text-xs text-muted-foreground/50">
                          —
                        </div>
                      )}
                      {dayEntries.map((entry) => (
                        <button
                          key={entry.id}
                          className="w-full text-left"
                          onClick={(e) => { e.stopPropagation(); openEditEntry(entry); }}
                        >
                          <div className="rounded-md bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors p-2 space-y-0.5">
                            <div className="text-xs font-bold text-primary truncate">
                              {entry.license_plate}
                            </div>
                            <div className="text-xs font-medium text-foreground truncate">
                              {entry.renter_name}
                            </div>
                            {entry.renter_phone && (
                              <div className="text-xs text-muted-foreground truncate">
                                {entry.renter_phone}
                              </div>
                            )}
                            {entry.price_cents != null && (
                              <div className="text-xs text-muted-foreground">
                                {(entry.price_cents / 100).toLocaleString('de-DE', {
                                  style: 'currency',
                                  currency: 'EUR',
                                })}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground/70">
                              {format(new Date(entry.date_start + 'T00:00:00'), 'dd.MM.')} –{' '}
                              {format(new Date(entry.date_end + 'T00:00:00'), 'dd.MM.')}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Right-click context menu */}
          {contextMenu && (
            <div
              className="absolute z-50 bg-popover border rounded-md shadow-lg py-1 min-w-[180px]"
              style={{ left: contextMenu.x, top: contextMenu.y }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-1.5 text-xs text-muted-foreground border-b mb-1">
                {format(new Date(contextMenu.date + 'T00:00:00'), 'dd. MMMM yyyy', { locale: de })}
              </div>
              <button
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent cursor-pointer"
                onClick={() => openNewEntry(contextMenu.date)}
              >
                <Plus className="h-4 w-4" />
                Mietfahrzeug eintragen
              </button>
            </div>
          )}
        </div>

        <CalendarEntryDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          entry={selectedEntry}
          defaultDate={defaultDate}
          onSuccess={handleDialogSuccess}
        />
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
